import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ActionButton, Button, InformationModal, PredefinedNotifierType} from 'components';
import {UserSessionContext} from 'context';
import {useApiData, useUserSession} from 'hooks';
import {SessionScreenshot} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {apiUtils, userAuthUtils} from 'utils';
import {transformUtils} from 'utils/transform';

import {ScreenshotPreview} from '../ScreenshotPreview';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.SCREENSHOTS_PREVIEW_DIALOG;

type Props = {
    screenshot: SessionScreenshot;
    onClose: () => void;
};

export const ScreenshotPreviewModal: React.FC<Props> = ({screenshot, onClose}: Props) => {
    const {t} = useTranslation();
    const {canManageSession} = useUserSession();
    const {deleteScreenshotInProgress, loadScreenshotPreview, onRequestDeleteScreenShot} = useContext(UserSessionContext);
    const onDelete = useCallback(() => onRequestDeleteScreenShot(screenshot), [screenshot, onRequestDeleteScreenShot]);
    const downloadDisabled = useMemo(() => userAuthUtils.isDownloadFileDisabledAuthSource(), []);
    const onDownloadSuccess = useCallback(({content}) =>
        apiUtils.saveFileFromBase64(`${screenshot.name}.png`, transformUtils.removeBase64Prefix(content)), [screenshot]);
    const downloadFn = useCallback(() => loadScreenshotPreview(screenshot), [screenshot, loadScreenshotPreview]);
    const {send: onDownload, loading: downloading} =
        useApiData(downloadFn, {onSuccess: onDownloadSuccess, notificationType: PredefinedNotifierType.FetchError});

    return (
        <InformationModal
            open={!!screenshot}
            fullWidth
            maxWidth='sm'
            title={screenshot?.name}
            onClose={onClose}
        >
            <div>
                <div className='tw-flex'>
                    {canManageSession &&
                        <ActionButton
                            action='delete'
                            loading={deleteScreenshotInProgress}
                            className='tw-mr-2'
                            onClick={onDelete}
                        >
                            {t(T_KEYS.BUTTONS.DELETE)}
                        </ActionButton>}
                    <Button
                        icon='arrow-down'
                        loading={downloading}
                        disabled={downloadDisabled}
                        onClick={onDownload}
                    >
                        {t(T_KEYS.BUTTONS.DOWNLOAD)}

                    </Button>
                </div>
                <div className='tw-mt-2'>
                    {screenshot && <ScreenshotPreview
                        screenshot={screenshot}
                        errorClassName='tw-border tw-border-default tw-p-2 tw-min-h-32'
                    />}
                </div>
            </div>
        </InformationModal>);
};
